import React from "react";
import moment from "moment";
import { Oval } from "react-loader-spinner";
import { BsPersonCheck, BsPersonPlus, BsX } from "react-icons/bs";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { Customer, Order } from "../../../../models";
import { triplePSDatasource } from "../../../../datasource";
import { getCustomers } from "../../../Customers/customersSlice";
import { updateOrder } from "../../ordersSlice";

import "./LinkCustomer.scss";

interface Props {
  order: Order;
}

// Admin control (Order Details header) to tie a guest / admin-created order to
// an EXISTING customer account. Linking only sets orders.user_id — the order's
// own contact fields (name / email / phone) are left exactly as they are.
export function LinkCustomer({ order }: Props) {
  const dispatch = useAppDispatch();
  const customers = useAppSelector((s) => s.customers.customers);
  const searching = useAppSelector((s) => s.customers.getCustomersResult.loading);

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [linked, setLinked] = React.useState<Customer | undefined>();
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);

  // Resolve the linked account so we can show who the order is tied to.
  React.useEffect(() => {
    let cancelled = false;
    if (order.userId) {
      triplePSDatasource
        .getCustomer(order.userId)
        .then((c) => {
          if (!cancelled) setLinked(c);
        })
        .catch(() => {
          if (!cancelled) setLinked(undefined);
        });
    } else {
      setLinked(undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [order.userId]);

  // Debounced customer search.
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (search.trim().length > 0) {
        dispatch(getCustomers({ search: search.trim(), offset: 0, count: 10 }));
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, dispatch]);

  const save = async (userId?: string) => {
    setSaving(true);
    try {
      // Full-replace PUT: echo the whole order, change only user_id. createdAt
      // is stripped by the thunk's UpdateOrderFormModel type. getOrder hands us
      // dates as MM-DD-YYYY, but the backend's ValidateUpdate wants YYYY-MM-DD
      // (Go 2006-01-02) — reformat or the PUT 3004s on the date.
      // (Success / error toasts come from the globally-mounted UpdateOrderModal.)
      await dispatch(
        updateOrder({
          ...order,
          userId,
          deliveryDate: moment(order.deliveryDate, "MM-DD-YYYY").format("YYYY-MM-DD"),
          pickupDate: moment(order.pickupDate, "MM-DD-YYYY").format("YYYY-MM-DD"),
        })
      ).unwrap();
      // The detail view updates in place via updateOrder.fulfilled (which now
      // syncs state.orders.order) — no refetch, so no loading-state flash.
      setOpen(false);
      setSearch("");
    } catch {
      // error toast handled globally by UpdateOrderModal's effect
    } finally {
      setSaving(false);
    }
  };

  const linkTo = (c: Customer) => save(c.id);
  const unlink = () => save(undefined);

  if (order.userId) {
    return (
      <div className="link-customer linked">
        <span className="lc-badge">
          <BsPersonCheck className="lc-badge-icon" />
          {linked ? (
            <span>
              Linked to <b>{linked.firstName} {linked.lastName}</b>
              {linked.email ? ` · ${linked.email}` : ""}
            </span>
          ) : (
            <span>Linked to a customer account</span>
          )}
        </span>
        <button className="lc-link-btn" onClick={unlink} disabled={saving}>
          {saving ? <Oval color="#FBD20A" height={12} width={12} secondaryColor="#131313" /> : "Unlink"}
        </button>
      </div>
    );
  }

  return (
    <div className="link-customer">
      <span className="lc-guest">Guest order — no account</span>
      {!open ? (
        <button className="lc-link-btn" onClick={() => setOpen(true)}>
          <BsPersonPlus className="lc-link-icon" /> Link to customer
        </button>
      ) : (
        <div className="lc-search">
          <div className="lc-search-row">
            <input
              className="lc-input"
              autoFocus
              type="text"
              placeholder="Search by name, email, or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              className="lc-close"
              aria-label="Close"
              onClick={() => {
                setOpen(false);
                setSearch("");
              }}
            >
              <BsX />
            </button>
          </div>
          {searching && (
            <div className="lc-loading">
              <Oval color="#FBD20A" height={18} width={18} secondaryColor="#131313" />
            </div>
          )}
          {!searching && search.trim().length > 0 && (
            <div className="lc-results">
              {customers.length === 0 ? (
                <div className="lc-empty">No customers found</div>
              ) : (
                customers.map((c) => (
                  <button
                    key={c.id}
                    className="lc-result"
                    disabled={saving}
                    onClick={() => linkTo(c)}
                  >
                    <span className="lc-result-name">
                      {c.firstName} {c.lastName}
                    </span>
                    <span className="lc-result-meta">
                      {c.email || c.phoneNumber}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
